import { type KeyboardEvent, type ReactNode, useCallback, useState } from "react";

import { cn } from "../../lib/cn";

type TabItem = {
	id: string;
	label: string;
	content: ReactNode;
};

type TabsProps = {
	tabs: Array<TabItem>;
	className?: string;
};

export function Tabs({ tabs, className }: TabsProps) {
	const [activeId, setActiveId] = useState(tabs[0]?.id ?? "");
	const activeTab = tabs.find((t) => t.id === activeId);
	const activeIndex = tabs.findIndex((t) => t.id === activeId);

	const handleKeyDown = useCallback(
		(e: KeyboardEvent) => {
			let nextIndex = activeIndex;
			if (e.key === "ArrowRight") {
				nextIndex = (activeIndex + 1) % tabs.length;
			} else if (e.key === "ArrowLeft") {
				nextIndex = (activeIndex - 1 + tabs.length) % tabs.length;
			} else if (e.key === "Home") {
				nextIndex = 0;
			} else if (e.key === "End") {
				nextIndex = tabs.length - 1;
			} else {
				return;
			}
			e.preventDefault();
			const nextTab = tabs[nextIndex];
			if (nextTab) {
				setActiveId(nextTab.id);
			}
		},
		[activeIndex, tabs]
	);

	return (
		<div className={className}>
			<div role="tablist" className="flex gap-1 border-b border-pastel-border">
				{tabs.map((tab) => (
					<button
						key={tab.id}
						type="button"
						role="tab"
						id={`tab-${tab.id}`}
						aria-selected={tab.id === activeId}
						aria-controls={`tabpanel-${tab.id}`}
						tabIndex={tab.id === activeId ? 0 : -1}
						onClick={() => setActiveId(tab.id)}
						onKeyDown={handleKeyDown}
						className={cn(
							"px-4 py-2 text-sm font-medium transition-colors -mb-px",
							tab.id === activeId
								? "border-b-2 border-pastel-vibrant-blue text-pastel-vibrant-blue"
								: "text-pastel-gray hover:text-pastel-black"
						)}
					>
						{tab.label}
					</button>
				))}
			</div>
			<div
				role="tabpanel"
				id={`tabpanel-${activeId}`}
				aria-labelledby={`tab-${activeId}`}
				className="pt-4"
			>
				{activeTab?.content}
			</div>
		</div>
	);
}
